import { BARCODE_HEADER_FOOTER_OPTIONS } from "@redotech/redo-fulfillment-common/barcode-options";
import { LabelExpirationStrategy } from "@redotech/redo-return-core-common/label-expiration-strategy";
import { formLabelInstructionsDefault } from "@redotech/redo-return-core-common/returns/default-texts";
import { isDefined } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  changedTeamPatchPaths,
  groupedSettingsChanges,
} from "./grouped-settings-changes";
import { DEFAULT_PACKING_INSTRUCTIONS } from "./packing-instruction-defaults";
import { ReturnMethod } from "./packing-instruction-return-method";

export const LABEL_EXPIRATION_MAX_DAYS = 30;
export const LABEL_CUSTOM_TEXT_MAX_LENGTH = 30;
const DEFAULT_REOPEN_RETURN_AFTER_DAYS = 10;

export const DEFAULT_LABEL_BARCODE = {
  height: "1.625",
  width: "3.125",
  topDisplayOption: BARCODE_HEADER_FOOTER_OPTIONS.PRODUCT_VARIANT_NAME,
  bottomDisplayOption: BARCODE_HEADER_FOOTER_OPTIONS.SKU_AND_WAREHOUSE,
};

const barcodeDimension = z
  .string()
  .refine((value) => Number(value) > 0, { message: "Must be positive" });

const instructionLines = z
  .array(z.string())
  .refine((lines) => lines.some((line) => line.trim()), {
    message: "Add at least one instruction",
  });

export const ReturnsLabelsSettingsSchema = z.object({
  expiration: z.object({
    days: z.number().int().min(1).max(LABEL_EXPIRATION_MAX_DAYS),
    strategy: createEnum(LabelExpirationStrategy),
    reopenWhenExpiredLabelUsed: z.boolean(),
    reopenAfterDays: z.number().int().min(1),
    flagReturnWhenReopened: z.boolean(),
  }),
  barcode: z.object({
    enabled: z.boolean(),
    height: barcodeDimension,
    width: barcodeDimension,
    topDisplayOption: createEnum(BARCODE_HEADER_FOOTER_OPTIONS),
    bottomDisplayOption: createEnum(BARCODE_HEADER_FOOTER_OPTIONS),
  }),
  labelBuilder: z.object({
    includeReturnType: z.boolean(),
    rmaBarcodeEnabled: z.boolean(),
    verifyAddressForLabel: z.boolean(),
    australiaPostPrinterlessEnabled: z.boolean(),
    australiaPostPrinterlessText: z.string(),
    qrCodeLabelInstructions: instructionLines,
    customTextLine1: z.string().max(LABEL_CUSTOM_TEXT_MAX_LENGTH),
    customTextLine2: z.string().max(LABEL_CUSTOM_TEXT_MAX_LENGTH),
  }),
  labelCombination: z.object({
    skuGroupingEnabled: z.boolean(),
    onlySameFulfillment: z.boolean(),
  }),
  packingSlips: z.object({
    includeInReturnLabel: z.boolean(),
    includeExchangeItems: z.boolean(),
    includeForInStoreDropOffs: z.boolean(),
    includeInManualLabel: z.boolean(),
  }),
  packingInstructions: z.object({
    customerShips: instructionLines,
    packagePickup: instructionLines,
  }),
});

export type ReturnsLabelsSettings = z.infer<typeof ReturnsLabelsSettingsSchema>;

const settingsShape = ReturnsLabelsSettingsSchema.shape;

/** Only the fields a merchant changed; everything omitted is left as stored. */
export const ReturnsLabelsSettingsChangesSchema = z.object({
  expiration: settingsShape.expiration.partial().optional(),
  barcode: settingsShape.barcode.partial().optional(),
  labelBuilder: settingsShape.labelBuilder.partial().optional(),
  labelCombination: settingsShape.labelCombination.partial().optional(),
  packingSlips: settingsShape.packingSlips.partial().optional(),
  packingInstructions: settingsShape.packingInstructions.partial().optional(),
});

export type ReturnsLabelsSettingsChanges = z.infer<
  typeof ReturnsLabelsSettingsChangesSchema
>;

/** Everything but expiration lives on the legacy Shipping page, which needs SET_SHIPPING_SETTINGS. */
export function returnsLabelsChangesNeedShippingPermission(
  changes: ReturnsLabelsSettingsChanges,
): boolean {
  return !!(
    changes.barcode ||
    changes.labelBuilder ||
    changes.labelCombination ||
    changes.packingSlips ||
    changes.packingInstructions
  );
}

export function returnsLabelsSettingsChanges(
  initial: ReturnsLabelsSettings,
  next: ReturnsLabelsSettings,
): ReturnsLabelsSettingsChanges {
  return ReturnsLabelsSettingsChangesSchema.parse(
    groupedSettingsChanges(initial, next),
  );
}

/** Not re-validated: a legacy stored value that fails today's schema must not block saving an unrelated field. */
function applyReturnsLabelsSettingsChanges(
  base: ReturnsLabelsSettings,
  changes: ReturnsLabelsSettingsChanges,
): ReturnsLabelsSettings {
  return {
    expiration: { ...base.expiration, ...changes.expiration },
    barcode: { ...base.barcode, ...changes.barcode },
    labelBuilder: { ...base.labelBuilder, ...changes.labelBuilder },
    labelCombination: { ...base.labelCombination, ...changes.labelCombination },
    packingSlips: { ...base.packingSlips, ...changes.packingSlips },
    packingInstructions: {
      ...base.packingInstructions,
      ...changes.packingInstructions,
    },
  };
}

/** Decimal128 on the server, `{ $numberDecimal }` once serialized to the client. */
type StoredDecimal = { $numberDecimal: string } | { toString(): string };

interface StoredBarcode {
  size: { height: { value: StoredDecimal }; width: { value: StoredDecimal } };
  topDisplayOption: BARCODE_HEADER_FOOTER_OPTIONS;
  bottomDisplayOption: BARCODE_HEADER_FOOTER_OPTIONS;
}

/** The slice of team settings the Labels tab reads. Structurally matches both the client team and the server's raw document. */
export interface ReturnsLabelsSettingsSource {
  labelExpiration: {
    days: number;
    strategy: LabelExpirationStrategy;
    shouldReopenReturnWhenLabelUsed: boolean;
    reopenReturnAfterDays: number | null;
    flagReturnWhenReopened: boolean;
    reminderEmailDays: number[];
  };
  products?: { barcode?: StoredBarcode | null } | null;
  labelIncludeReturnType?: boolean | null;
  packingSlipEnabled?: boolean | null;
  includeExchangeItemsOnPackingSlip?: boolean | null;
  inStoreDropOffPackingSlipEnabled?: boolean | null;
  manualLabelPackingSlipEnabled?: boolean | null;
  packingInstructions?: { [key in ReturnMethod]?: string[] } | string[] | null;
  returns?: {
    formLabelInstructions?: string[] | null;
    australiaPostPrinterless?: {
      enabled?: boolean | null;
      text?: string | null;
    } | null;
    labelCustomText?: { line1?: string | null; line2?: string | null } | null;
    labelRmaBarcodeEnabled?: boolean | null;
    verifyAddressForLabel?: boolean | null;
    labelCombination?: {
      combineLabelsSkuGroups?: { enabled?: boolean | null } | null;
      onlyCombineLabelsFromSameFulfillment?: boolean | null;
    } | null;
  } | null;
}

function decimalText(value: StoredDecimal): string {
  return "$numberDecimal" in value ? value.$numberDecimal : value.toString();
}

function storedPackingInstructions(
  settings: ReturnsLabelsSettingsSource,
): { [key in ReturnMethod]?: string[] } | undefined {
  const stored = settings.packingInstructions;
  return stored && !Array.isArray(stored) ? stored : undefined;
}

function nonEmptyOr(lines: string[] | null | undefined, fallback: string[]) {
  return lines?.length ? lines : fallback;
}

export function returnsLabelsSettingsFromTeam(
  settings: ReturnsLabelsSettingsSource,
): ReturnsLabelsSettings {
  const { labelExpiration, returns } = settings;
  const barcode = settings.products?.barcode;
  const packingInstructions = storedPackingInstructions(settings);
  return {
    expiration: {
      days: labelExpiration.days,
      strategy: labelExpiration.strategy,
      reopenWhenExpiredLabelUsed:
        labelExpiration.shouldReopenReturnWhenLabelUsed,
      reopenAfterDays:
        labelExpiration.reopenReturnAfterDays ??
        DEFAULT_REOPEN_RETURN_AFTER_DAYS,
      flagReturnWhenReopened: labelExpiration.flagReturnWhenReopened,
    },
    barcode: barcode
      ? {
          enabled: true,
          height: decimalText(barcode.size.height.value),
          width: decimalText(barcode.size.width.value),
          topDisplayOption: barcode.topDisplayOption,
          bottomDisplayOption: barcode.bottomDisplayOption,
        }
      : { enabled: false, ...DEFAULT_LABEL_BARCODE },
    labelBuilder: {
      includeReturnType: settings.labelIncludeReturnType !== false,
      rmaBarcodeEnabled: !!returns?.labelRmaBarcodeEnabled,
      verifyAddressForLabel: returns?.verifyAddressForLabel ?? true,
      australiaPostPrinterlessEnabled:
        !!returns?.australiaPostPrinterless?.enabled,
      australiaPostPrinterlessText:
        returns?.australiaPostPrinterless?.text ?? "",
      qrCodeLabelInstructions: nonEmptyOr(
        returns?.formLabelInstructions,
        formLabelInstructionsDefault(),
      ),
      customTextLine1: returns?.labelCustomText?.line1 ?? "",
      customTextLine2: returns?.labelCustomText?.line2 ?? "",
    },
    labelCombination: {
      skuGroupingEnabled:
        !!returns?.labelCombination?.combineLabelsSkuGroups?.enabled,
      onlySameFulfillment:
        !!returns?.labelCombination?.onlyCombineLabelsFromSameFulfillment,
    },
    packingSlips: {
      includeInReturnLabel: !!settings.packingSlipEnabled,
      includeExchangeItems: !!settings.includeExchangeItemsOnPackingSlip,
      includeForInStoreDropOffs: !!settings.inStoreDropOffPackingSlipEnabled,
      includeInManualLabel: settings.manualLabelPackingSlipEnabled ?? true,
    },
    packingInstructions: {
      customerShips: [
        ...nonEmptyOr(packingInstructions?.[ReturnMethod.CUSTOMER_SHIPS], [
          ...DEFAULT_PACKING_INSTRUCTIONS[ReturnMethod.CUSTOMER_SHIPS],
        ]),
      ],
      packagePickup: [
        ...nonEmptyOr(packingInstructions?.[ReturnMethod.PACKAGE_PICKUP], [
          ...DEFAULT_PACKING_INSTRUCTIONS[ReturnMethod.PACKAGE_PICKUP],
        ]),
      ],
    },
  };
}

export interface ReturnsLabelsTeamPatch {
  /** Dotted paths relative to `team.settings`. */
  set: Record<string, unknown>;
  unset: string[];
}

function withoutBlankLines(lines: string[]): string[] {
  return lines.filter((line) => line.trim());
}

export function returnsLabelsSettingsToTeamPatch(
  settings: ReturnsLabelsSettingsSource,
  next: ReturnsLabelsSettings,
): ReturnsLabelsTeamPatch {
  const set: Record<string, unknown> = {
    "labelExpiration.days": next.expiration.days,
    "labelExpiration.strategy": next.expiration.strategy,
    "labelExpiration.shouldReopenReturnWhenLabelUsed":
      next.expiration.reopenWhenExpiredLabelUsed,
    "labelExpiration.reopenReturnAfterDays": next.expiration.reopenAfterDays,
    "labelExpiration.flagReturnWhenReopened":
      next.expiration.flagReturnWhenReopened,
    labelIncludeReturnType: next.labelBuilder.includeReturnType,
    "returns.labelRmaBarcodeEnabled": next.labelBuilder.rmaBarcodeEnabled,
    "returns.verifyAddressForLabel": next.labelBuilder.verifyAddressForLabel,
    "returns.labelCombination.combineLabelsSkuGroups.enabled":
      next.labelCombination.skuGroupingEnabled,
    "returns.labelCombination.onlyCombineLabelsFromSameFulfillment":
      next.labelCombination.onlySameFulfillment,
    "returns.australiaPostPrinterless.enabled":
      next.labelBuilder.australiaPostPrinterlessEnabled,
    "returns.formLabelInstructions": withoutBlankLines(
      next.labelBuilder.qrCodeLabelInstructions,
    ),
    packingSlipEnabled: next.packingSlips.includeInReturnLabel,
    includeExchangeItemsOnPackingSlip: next.packingSlips.includeExchangeItems,
    inStoreDropOffPackingSlipEnabled:
      next.packingSlips.includeForInStoreDropOffs,
    manualLabelPackingSlipEnabled: next.packingSlips.includeInManualLabel,
  };
  const unset: string[] = [];

  if (next.expiration.days !== settings.labelExpiration.days) {
    set["labelExpiration.reminderEmailDays"] =
      settings.labelExpiration.reminderEmailDays.filter(
        (day) => day <= next.expiration.days,
      );
  }

  if (next.barcode.enabled) {
    set["products.barcode"] = {
      size: {
        height: {
          value: { $numberDecimal: next.barcode.height.trim() },
          unit: "in",
        },
        width: {
          value: { $numberDecimal: next.barcode.width.trim() },
          unit: "in",
        },
      },
      topDisplayOption: next.barcode.topDisplayOption,
      bottomDisplayOption: next.barcode.bottomDisplayOption,
    };
  } else if (isDefined(settings.products?.barcode)) {
    unset.push("products.barcode");
  }

  const setOrUnset = (path: string, value: string, stored: unknown) => {
    const trimmed = value.trim();
    if (trimmed) {
      set[path] = trimmed;
    } else if (isDefined(stored)) {
      unset.push(path);
    }
  };
  const { returns } = settings;
  setOrUnset(
    "returns.australiaPostPrinterless.text",
    next.labelBuilder.australiaPostPrinterlessText,
    returns?.australiaPostPrinterless?.text,
  );
  setOrUnset(
    "returns.labelCustomText.line1",
    next.labelBuilder.customTextLine1,
    returns?.labelCustomText?.line1,
  );
  setOrUnset(
    "returns.labelCustomText.line2",
    next.labelBuilder.customTextLine2,
    returns?.labelCustomText?.line2,
  );

  const customerShips = withoutBlankLines(
    next.packingInstructions.customerShips,
  );
  const packagePickup = withoutBlankLines(
    next.packingInstructions.packagePickup,
  );
  const storedInstructions = storedPackingInstructions(settings);
  if (storedInstructions) {
    set[`packingInstructions.${ReturnMethod.CUSTOMER_SHIPS}`] = customerShips;
    set[`packingInstructions.${ReturnMethod.PACKAGE_PICKUP}`] = packagePickup;
  } else {
    // A legacy array (or nothing) can't take dotted keys, so write the whole object.
    set.packingInstructions = {
      [ReturnMethod.CUSTOMER_SHIPS]: customerShips,
      [ReturnMethod.CUSTOMER_IN_STORE]: [
        ...DEFAULT_PACKING_INSTRUCTIONS[ReturnMethod.CUSTOMER_IN_STORE],
      ],
      [ReturnMethod.PACKAGE_PICKUP]: packagePickup,
    };
  }

  return { set, unset };
}

/**
 * Writes only what the merchant changed. Untouched fields are rebuilt from the
 * stored settings, so a stale form can't revert edits made elsewhere.
 */
export function returnsLabelsTeamPatchForChanges(
  settings: ReturnsLabelsSettingsSource,
  changes: ReturnsLabelsSettingsChanges,
): ReturnsLabelsTeamPatch {
  const storedValue = returnsLabelsSettingsFromTeam(settings);
  return changedTeamPatchPaths(
    returnsLabelsSettingsToTeamPatch(settings, storedValue),
    returnsLabelsSettingsToTeamPatch(
      settings,
      applyReturnsLabelsSettingsChanges(storedValue, changes),
    ),
  );
}
